import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Genies.css';
import './InsightsPage.css';
import './AgentTile.css';
import { useGenies } from './GenieContext';
import api from '../api';

// Category metadata: subtitle label and description per category
const CATEGORY_META = {
  'Ecolane': {
    subtitle: 'Ecolane',
    descriptionFn: (name) => `Explore ${name} data with AI-powered analytics and insights`
  },
  'Tripmaster': {
    subtitle: 'TripMaster',
    descriptionFn: (name) => `Analyze ${name} scheduling data and uncover operational trends`
  },
  'FASTER': {
    subtitle: 'FASTER',
    descriptionFn: (name) => `Query ${name} fleet and maintenance data with natural language`
  },
  'Other': {
    subtitle: '',
    descriptionFn: (name) => `Ask questions and get AI-powered insights from ${name}`
  }
};

/**
 * Format an ISO timestamp into a human-readable relative time string.
 * e.g. "just now", "5m ago", "2 hrs ago", "3 days ago"
 */
function formatLastUsed(isoString) {
  if (!isoString) return null;
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;
  if (diffMs < 0) return 'just now';
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return diffHr === 1 ? '1 hr ago' : `${diffHr} hrs ago`;
  const diffDays = Math.floor(diffHr / 24);
  return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
}

function Genies() {
  const context = useGenies();
  const contextGenies = context?.genies;
  const isLoading = context?.isLoading || false;
  const [searchQuery, setSearchQuery] = useState('');
  const [lastUsedMap, setLastUsedMap] = useState({});

  // Group API genies (and agents) by category
  const groupedGenies = useMemo(() => {
    const items = contextGenies || [];
    const groups = {};

    items.forEach(item => {
      let category = 'Other';
      const name = item.name.toLowerCase();

      if (name.includes('faster') || name.includes('asset intelligence')) {
        category = 'FASTER';
      } else if (name.includes('ecolane')) {
        category = 'Ecolane';
      } else if (name.includes('tripmaster') || name.includes('trip master')) {
        category = 'Tripmaster';
      }

      if (!groups[category]) {
        groups[category] = [];
      }

      // Agents link to /agent/:id, genies link to /chat/:id
      const isAgent = item.type === 'agent';
      groups[category].push({
        id: item.id,
        name: item.name,
        link: isAgent ? `/agent/${item.id}` : `/chat/${item.id}`,
        isAgent,
        endpointName: item.endpointName,
      });
    });

    return groups;
  }, [contextGenies]);

  // Fetch last-used timestamps for all genies once on mount
  useEffect(() => {
    api.get('/api/conversations/last-used')
      .then(res => {
        if (res.data?.lastUsed) {
          setLastUsedMap(res.data.lastUsed);
        }
      })
      .catch(err => {
        console.warn('[Genies] Could not fetch last-used times:', err.message);
      });
  }, []);

  // Filter genies based on search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groupedGenies;

    const q = searchQuery.toLowerCase();
    const filtered = {};
    Object.keys(groupedGenies).forEach(category => {
      const matches = groupedGenies[category].filter(g =>
        g.name.toLowerCase().includes(q) ||
        category.toLowerCase().includes(q)
      );
      if (matches.length > 0) filtered[category] = matches;
    });
    return filtered;
  }, [groupedGenies, searchQuery]);

  // Sort categories so agent-containing categories appear first
  const categoryKeys = Object.keys(filteredGroups).sort((a, b) => {
    const aHasAgent = filteredGroups[a].some(item => item.isAgent);
    const bHasAgent = filteredGroups[b].some(item => item.isAgent);
    if (aHasAgent && !bHasAgent) return -1;
    if (!aHasAgent && bHasAgent) return 1;
    return 0;
  });

  return (
    <>
      <div className="genies-background" />
      <div className="genies-container insights-page" style={{ textAlign: 'left' }}>

        {/* Page Header — matches InsightsPage */}
        <div className="insights-page-header">
          <h1 className="insights-page-title">AI Studio</h1>
          <p className="insights-page-subtitle">Select a Genie to analyze your data and generate insights</p>
        </div>

        {/* Search bar */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '40px' }}>
          <div className="genies-container-search-bar">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search genies…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="genies-search-field"
              aria-label="Search genies"
              spellCheck={false}
            />
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="loading-genies-message">
            <p>Loading genies...</p>
          </div>
        ) : !contextGenies?.length ? (
          <div className="no-genies-message">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" style={{ marginBottom: '16px', opacity: 0.3 }}>
              <circle cx="32" cy="32" r="30" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4"/>
              <path d="M32 20v24M20 32h24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <h2>No Genies Available</h2>
            <p>You don't have any genies mapped to your account yet.</p>
            <p style={{ marginTop: '8px' }}>Please contact your administrator to get access.</p>
          </div>
        ) : categoryKeys.length === 0 ? (
          <div className="no-genies-message">
            <p>No genies found matching "{searchQuery}"</p>
          </div>
        ) : (
          categoryKeys.map(category => {
            const meta = CATEGORY_META[category] || CATEGORY_META['Other'];
            return (
              <div key={category} className="insights-section">
                {/* Section header — matches InsightsPage section divider */}
                <div className="insights-section-header">
                  <h2 className="insights-section-title">{category}</h2>
                </div>

                {/* Tiles grid — reuses insights-products-grid + insights-product-tile */}
                <div className="insights-products-grid" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {[...filteredGroups[category]].sort((a, b) => {
                    if (a.isAgent && !b.isAgent) return -1;
                    if (!a.isAgent && b.isAgent) return 1;
                    return 0;
                  }).map(item => (
                    <Link
                      key={item.id}
                      to={item.link}
                      className={`insights-product-tile${item.isAgent ? ' insights-product-tile--agent' : ''}`}
                    >
                      <div className="insights-product-content">
                        <div className="insights-product-subtitle-row">
                          {meta.subtitle && (
                            <span className="insights-product-subtitle">{meta.subtitle}</span>
                          )}
                          {item.isAgent && (
                            <span className="insights-agent-badge">
                              <span className="insights-agent-badge-icon">⚡</span>
                              Multi-Agent AI
                            </span>
                          )}
                        </div>
                        <div className="insights-product-name-row">
                          <h3 className="insights-product-name">{item.name}</h3>
                        </div>
                        <p className="insights-product-description">
                          {item.isAgent
                            ? `AI supervisor that coordinates multiple specialized analysts for ${meta.subtitle || 'fleet'} data`
                            : meta.descriptionFn(item.name)
                          }
                        </p>
                      </div>
                      <div className="insights-product-footer">
                        <div className="insights-footer-left">
                          {lastUsedMap[item.id] ? (
                            <span className="genie-last-used">
                              Used {formatLastUsed(lastUsedMap[item.id])}
                            </span>
                          ) : (
                            <span className="genie-last-used genie-last-used--never">
                              Never used
                            </span>
                          )}
                        </div>
                        <span className="insights-launch-btn">{item.isAgent ? 'Chat' : 'Launch'}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })
        )}

      </div>
    </>
  );
}

export default Genies;
